/**
 * Enterprise Lesson Audio — step narration playback.
 * Auto-plays audio when a step has an audioUrl.
 * Fires onEnded callback when playback completes (not on autoplay block).
 */

let currentAudio: HTMLAudioElement | null = null;
let currentUrl: string | null = null;
let endedCallback: (() => void) | null = null;

export function playStepAudio(url?: string, onEnded?: () => void): void {
  // If audio is still playing from the previous step, let it finish naturally
  if (currentAudio && !currentAudio.paused) {
    // Don't start the new step's audio — let the current track finish
    endedCallback = onEnded ?? null;
    if (!url) {
      // No new audio and old one is playing — callback fires when old one ends
    }
    return;
  }

  stopAudio();
  endedCallback = onEnded ?? null;
  if (!url) {
    // No audio — fire ended immediately so Next isn't blocked
    if (endedCallback) endedCallback();
    return;
  }
  currentUrl = url;
  currentAudio = new Audio(url);
  currentAudio.addEventListener('ended', () => {
    if (endedCallback) endedCallback();
    endedCallback = null;
  });
  currentAudio.play().catch(() => {
    // Autoplay blocked — do NOT enable Next.
    // User must click the audio button to start playback (user gesture).
  });
}

/** Try to resume/replay current audio (called from audio button click — user gesture). */
export function resumeAudio(): void {
  if (currentAudio && currentAudio.paused) {
    currentAudio.play().catch(() => {});
  }
}

export function stopAudio(): void {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  currentUrl = null;
  endedCallback = null;
}

export function isPlaying(): boolean {
  return currentAudio !== null && !currentAudio.paused;
}

/** Whether audio exists but is paused (autoplay blocked). */
export function isPaused(): boolean {
  return currentAudio !== null && currentAudio.paused;
}
